import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, from, of } from "rxjs";
import {
  EnvironmentConfig,
  BaseApiService,
  AuthenticationService,
  SearchResult,
  SearchModel,
  Location,
  LocationPipe,
  LocalStorageService,
  LogService,
} from "../../core/core";
import { InventoryItem, InventoryReview, Specification, Attachment } from "../inventory";
import { AssociatedItem } from "../models/associated-item.model";
import { AttachmentViewModel } from "../models/atachment-view-model.model";
import { Device } from "../models/device.model";
import { catchError, map, tap } from "rxjs/operators";
import { Operation } from "fast-json-patch";
import { OpenAIService, MachineAnalysisResult, MachineTypeDefinitionOption } from "../../rubbl-shared/services/open-ai.service";

declare var environment: EnvironmentConfig;

@Injectable({
  providedIn: "root",
})
export class InventoryService extends BaseApiService<InventoryItem> {
  baseUrlForIcons = "https://company.rubbl.com/pubweb/marketplace/homepage/popular-categories/";

  constructor(
    httpClient: HttpClient,
    private authenticationService: AuthenticationService,
    private locationPipe: LocationPipe,
    private localStorageService: LocalStorageService,
    private logService: LogService,
    private openAIService: OpenAIService,
  ) {
    super(httpClient, environment.assetsApiDomain, "/api/assets/v2/inventory", true, authenticationService);
    this.baseUrl = this.baseUrl.replace(/\/+$/, ""); // remove trailing slash
  }

  getCategoryPictureUrl(category: string): string {
    const formattedCategory = category.toLowerCase().replace(/ /g, "_");
    return `${this.baseUrlForIcons}${formattedCategory}.png`;
  }

  create(model: any): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient.post<any>(`${this.baseUrl}${this.endpoint}`, model, { headers });
  }

  getWithPartitionKey(id: string, partitionKey: string): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient.get<any>(`${this.baseUrl}${this.endpoint}/${id}/details?partitionKey=${partitionKey}`, { headers });
  }

  update(id: string, model: any): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient
      .put<any>(`${this.baseUrl}${this.endpoint}/${id}/details?partitionKey=${model.accountId}`, model, { headers })
      .pipe(
        tap(result => {
          if (model.inventoryItemType == "Attachment") {
            return;
          }

          this.updateLastModifiedByOwner(id, model.accountId).subscribe();
        }),
      );
  }

  getShippingDimensions(id: string, partitionKey: string) {
    const headers = this.getAuth();
    return this.httpClient
      .get<any>(`${this.baseUrl}${this.endpoint}/${id}/shipping?accountId=${partitionKey}`, { headers })
      .pipe(
        catchError(err => {
          alert("Machine does not have shipping dimensions. Click edit and sync shipping dimensions from catalog");
          return of({});
        }),
      );
  }

  updateShippingDimensions(shippingDimensions: any) {
    const headers = this.getAuth();
    return this.httpClient.put<any>(
      `${this.baseUrl}${this.endpoint}/${shippingDimensions.inventoryItemId}/shipping?accountId=${shippingDimensions.accountId}`,
      shippingDimensions,
      { headers },
    );
  }

  getImages(inventoryId: string, accountId: string, orderByCover: boolean = false, privateImage: boolean = false) {
    const headers = this.getAuth();
    let params = new HttpParams();
    params = params.append("accountId", accountId);
    params = params.append("orderByCover", orderByCover.toString());
    params = params.append("privateImage", privateImage.toString());

    return this.httpClient.get<any>(`${this.baseUrl}${this.endpoint}/${inventoryId}/image`, { headers, params });
  }

  uploadImage(inventoryId: string, accountId: string, position: number, selectedFile, privateImage: boolean = false): Observable<any> {
    const uploadData = new FormData();
    uploadData.append("file", selectedFile);

    let params = new HttpParams();
    params = params.append("accountId", accountId);
    params = params.append("position", position);
    params = params.append("privateImage", privateImage);

    const headers = this.getAuth();
    return this.httpClient.post(`${this.baseUrl}${this.endpoint}/${inventoryId}/image`, uploadData, { headers, params }).pipe(
      tap(result => {
        this.updateLastModifiedByOwner(inventoryId, accountId).subscribe();
      }),
    );
  }

  replaceImage(inventoryId: string, accountId: string, position: number, selectedFile): Observable<any> {
    const uploadData = new FormData();
    uploadData.append("file", selectedFile);

    const headers = this.getAuth();
    return this.httpClient
      .put(`${this.baseUrl}${this.endpoint}/${inventoryId}/image?accountId=${accountId}&position=${position}`, uploadData, { headers })
      .pipe(
        tap(result => {
          this.updateLastModifiedByOwner(inventoryId, accountId).subscribe();
        }),
      );
  }

  updateCoverImage(inventoryId: string, accountId: string, position: number) {
    const headers = this.getAuth();
    return this.httpClient
      .patch(`${this.baseUrl}${this.endpoint}/${inventoryId}/image/cover?accountId=${accountId}&position=${position}`, {}, { headers })
      .pipe(
        tap(result => {
          this.updateLastModifiedByOwner(inventoryId, accountId).subscribe();
        }),
      );
  }

  deleteImage(inventoryId: string, accountId: string, position: number, privateImage: boolean = false) {
    const headers = this.getAuth();
    let params = new HttpParams();
    params = params.append("accountId", accountId);
    params = params.append("privateImage", privateImage);

    return this.httpClient.delete(`${this.baseUrl}${this.endpoint}/${inventoryId}/image/${position}`, { headers, params }).pipe(
      tap(result => {
        this.updateLastModifiedByOwner(inventoryId, accountId).subscribe();
      }),
    );
  }

  deleteImageById(imageId: string, partitionKey: string) {
    const headers = this.getAuth();
    let params = new HttpParams();
    params = params.append("partitionKey", partitionKey);

    return this.httpClient.delete(`${this.baseUrl}/api/assets/v2/image/${imageId}`, { headers, params });
  }

  getReviews(machineId: string): Observable<SearchResult<InventoryReview>> {
    const searchModel = new SearchModel();
    searchModel["hasComments"] = true;
    return this.httpClient.get<SearchResult<InventoryReview>>(`${this.baseUrl}${this.endpoint}/${machineId}/review`, { params: searchModel as any });
  }

  getEquipmentTypeInfo(): Observable<any> {
    return this.httpClient.get<SearchResult<InventoryReview>>(`${this.baseUrl}/api/assets/v1/config/public/equipmenttype`);
  }

  getAttachments(inventoryId: string): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient.get<any>(`${this.baseUrl}${this.endpoint}/${inventoryId}/attachment`, { headers });
  }

  getAttachmentAssociation(inventoryId: string, attachmentAssociationId: string, accountId: string): Observable<AssociatedItem> {
    const headers = this.getAuth();
    return this.httpClient.get<AssociatedItem>(
      `${this.baseUrl}${this.endpoint}/${inventoryId}/attachment/${attachmentAssociationId}?accountId=${accountId}`,
      { headers },
    );
  }

  addAttachment(inventoryId: string, item: AssociatedItem): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient.post(`${this.baseUrl}${this.endpoint}/${inventoryId}/attachment`, item, { headers }).pipe(
      tap(result => {
        this.updateLastModifiedByOwner(inventoryId, item.accountId).subscribe();
      }),
    );
  }

  updateAttachment(inventoryId: string, attachmentAssociationId: string, item: AttachmentViewModel): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient.put(`${this.baseUrl}${this.endpoint}/${inventoryId}/attachment/${attachmentAssociationId}`, item, { headers }).pipe(
      tap(result => {
        this.updateLastModifiedByOwner(inventoryId, item.accountId).subscribe();
      }),
    );
  }

  deleteAttachment(inventoryId: string, attachmentAssociationId: string, accountId: string): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient
      .delete(`${this.baseUrl}${this.endpoint}/${inventoryId}/attachment/${attachmentAssociationId}?accountId=${accountId}`, { headers })
      .pipe(
        tap(result => {
          this.updateLastModifiedByOwner(inventoryId, accountId).subscribe();
        }),
      );
  }

  getTelematics(inventoryId: string): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient.get<any>(`${this.baseUrl}${this.endpoint}/${inventoryId}/telematics`, { headers });
  }

  setTelematics(inventoryId: string, device: Device): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient.put<any>(`${this.baseUrl}${this.endpoint}/${inventoryId}/telematics`, device, { headers });
  }

  getLocation(inventoryId: string): Observable<Location> {
    const headers = this.getAuth();
    return this.httpClient.get<any>(`${this.baseUrl}${this.endpoint}/${inventoryId}/location`, { headers });
  }

  downloadAllPhotos(inventoryId: string) {
    const headers = this.getAuth();

    const requestOptions: Object = {
      headers,
      responseType: "arraybuffer",
    };

    return this.httpClient.get<any>(`${this.baseUrl}${this.endpoint}/${inventoryId}/image/archive`, requestOptions).pipe(
      map(result => {
        let blob = new Blob([result], { type: "application/zip" });
        let url = window.URL.createObjectURL(blob);
        window.open(url);
      }),
    );
  }

  getFormattedSpecifications(specifications: Specification[] | any[] = []): string {
    const specNamesAndValues = specifications["flatMap"](d => d.items).map(d => {
      return `${d.name}: ${d.value}`;
    });
    return specNamesAndValues.join(", ");
  }

  machineDetailsUrl(inventoryItem: InventoryItem, includeId: boolean = true, contextUrl?: string): string {
    // Determine URL prefix based on context or equipment availability
    let urlPrefix = '/equipment-rental'; // Default fallback
    let queryParams: string[] = [];

    // If we have context from the current URL, use that
    if (contextUrl) {
      if (contextUrl.startsWith('/equipment-for-sale')) {
        urlPrefix = '/equipment-for-sale';
      } else if (contextUrl.startsWith('/equipment-rental')) {
        urlPrefix = '/equipment-rental';
        // Preserve rpoEnabled query param if present in context
        if (contextUrl.includes('rpoEnabled=true')) {
          queryParams.push('rpoEnabled=true');
        }
      } else if (contextUrl.startsWith('/equipment-rentals')) {
        urlPrefix = '/equipment-rental';
        // Preserve rpoEnabled query param if present in context
        if (contextUrl.includes('rpoEnabled=true')) {
          queryParams.push('rpoEnabled=true');
        }
      } else if (contextUrl.startsWith('/equipment')) {
        urlPrefix = '/equipment';
      }
    } else {
      // No context provided, determine from machine properties
      if (inventoryItem.buyItNowOnly) {
        urlPrefix = '/equipment-for-sale';
      } else if (!inventoryItem.rentalRate && !inventoryItem.rpoEnabled && inventoryItem.buyItNowEnabled) {
        // Only buy-it-now, no rental or RPO
        urlPrefix = '/equipment-for-sale';
      } else if (inventoryItem.rpoEnabled && !inventoryItem.buyItNowEnabled) {
        // Rent-to-own enabled, but no buy-it-now (may or may not have rental)
        urlPrefix = '/equipment-rental';
        queryParams.push('rpoEnabled=true');
      } else if (inventoryItem.rentalRate > 0 && !inventoryItem.rpoEnabled && !inventoryItem.buyItNowEnabled) {
        // Only rental, no RPO or buy-it-now
        urlPrefix = '/equipment-rental';
      }
      // Otherwise use /equipment-rental (mixed availability or all three options)
    }

    let seourl = `${urlPrefix}/${this.locationPipe.transform(inventoryItem.location, "cityState", true).replace(", ", "-")}/${inventoryItem.primaryType}/${
      inventoryItem.make
    }/${inventoryItem.model}`;

    if (includeId) {
      queryParams.push(`id=${inventoryItem.id}`);
    }

    if (queryParams.length > 0) {
      seourl += `?${queryParams.join('&')}`;
    }
    return seourl;
  }

  notifyAdmin(id: string, partitionKey: string) {
    const jsonPatchOperation = [
      { op: "replace", path: "/approvalStatus", value: "Pending" },
      { op: "replace", path: "/lastUpdatedByOwnerDateTime", value: new Date() },
    ];

    const headers = this.getAuth();
    return this.httpClient.patch(`${this.baseUrl}${this.endpoint}/${id}?partitionKey=${partitionKey}&sendNotification=true`, jsonPatchOperation, { headers });
  }

  approve(inventoryItemId: string, accountId: string, approvalStatus: InventoryItem["approvalStatus"], reason?: string) {
    const headers = this.getAuth();

    return this.httpClient.post<any>(
      `${this.baseUrl}${this.endpoint}/approval`,
      {
        inventoryItemId,
        accountId,
        approvalStatus,
        approvalReason: reason,
      },
      { headers },
    );
  }

  jsonPatchWithPartitionKey(id: string, jsonPatchOperations: Operation[] | any[], partitionKey: string, partitionPath: string = ""): Observable<any> {
    const headers = this.getAuth();

    return this.httpClient
      .patch<any>(`${this.baseUrl}${this.endpoint}/${id}${partitionPath}?partitionKey=${partitionKey}`, jsonPatchOperations, { headers })
      .pipe(
        tap(result => {
          this.updateLastModifiedByOwner(id, partitionKey).subscribe();
        }),
      );
  }

  updateLastModifiedByOwner(id: string, partitionKey: string) {
    if (environment.clientId != "rubbl.website") {
      return of(true);
    }
    const jsonPatchOperation = [{ op: "replace", path: "/lastUpdatedByOwnerDateTime", value: new Date() }];

    const headers = this.getAuth();
    return this.httpClient.patch(`${this.baseUrl}${this.endpoint}/${id}?partitionKey=${partitionKey}&sendNotification=true`, jsonPatchOperation, { headers });
  }

  getSpecs(inventoryId: string, partitionKey: string) {
    const headers = this.getAuth();
    return this.httpClient.get<any>(`${this.baseUrl}/api/assets/v2/specifications/${inventoryId}?partitionKey=${partitionKey}`, { headers });
  }

  detectDecals(inventoryId: string, accountId: string, formData: FormData): Observable<{ masks: any[]; maskImage?: { fullSize: string; thumb: string } }> {
    const url = `${this.baseUrl}/${this.endpoint}/${inventoryId}/image/detect-decals?accountId=${accountId}`;
    const headers = this.getAuth();
    return this.httpClient.post<{ masks: any[]; maskImage?: { fullSize: string; thumb: string } }>(url, formData, { headers });
  }

  blurDecals(inventoryId: string, accountId: string, formData: FormData): Observable<{ fullSize: string; thumb: string }> {
    const url = `${this.baseUrl}/${this.endpoint}/${inventoryId}/image/blur?accountId=${accountId}`;
    const headers = this.getAuth();
    return this.httpClient.post<{ fullSize: string; thumb: string }>(url, formData, { headers });
  }

  inpaint(inventoryId: string, accountId: string, formData: FormData): Observable<{ fullSize: string; thumb: string }> {
    const url = `${this.baseUrl}/${this.endpoint}/${inventoryId}/image/inpaint?accountId=${accountId}`;
    const headers = this.getAuth();
    return this.httpClient.post<{ fullSize: string; thumb: string }>(url, formData, { headers });
  }

  generateHorizontalComposite(inventoryId: string, accountId: string, file: File): Observable<{ fullSize: string; thumb: string }> {
    const formData = new FormData();
    formData.append("imageFile", file);
    const url = `${this.baseUrl}${this.endpoint}/${inventoryId}/image/composite`; // Updated endpoint
    let params = new HttpParams().set("accountId", accountId);
    const headers = this.getAuth();
    return this.httpClient.post<{ fullSize: string; thumb: string }>(url, formData, { headers, params });
  }

  /**
   * Calculate weekly and daily rental rates based on monthly rate and account type
   * @param monthlyRate - The monthly rental rate
   * @param accountType - The account type (RentalCompany, Dealer, or other)
   * @returns Object containing weekly and daily rates
   */
  calculateRentalRates(monthlyRate: number, accountType: string): { weekly: number; daily: number } {
    const isRentalCompany = accountType === "RentalCompany" || accountType === "Dealer";
    const weeklyFactor = isRentalCompany ? 0.333333 : 0.2888;
    const dailyFactor = isRentalCompany ? 0.15 : 0.14;

    return {
      weekly: Math.round(monthlyRate * weeklyFactor),
      daily: Math.round(monthlyRate * dailyFactor),
    };
  }

  /**
   * Get the sell it now fee percentage based on account type
   * Fee: 6.5% for Dealers/Rental Companies, 10% for others
   * @param companyType - The company type from the account
   * @returns Fee percentage (6.5 or 10)
   */
  getSellItNowFeePercentage(companyType: string): number {
    const isDealerOrRentalCompany = companyType === "RentalCompany" || companyType === "Dealer";
    return isDealerOrRentalCompany ? 6.5 : 10;
  }

  /**
   * Calculate sale proceeds after Rubbl fee
   * @param salePrice - The sale price
   * @param companyType - The company type from the account
   * @returns Sale proceeds after fee deduction
   */
  calculateSaleProceeds(salePrice: number, companyType: string): number {
    if (!salePrice) return 0;
    const feePercentage = this.getSellItNowFeePercentage(companyType) / 100;
    return Math.round(salePrice * (1 - feePercentage));
  }

  /**
   * Analyze a machine image using OpenAI to identify machine details and suggest rental rates
   * @param imageFile - The image file to analyze
   * @param availableMachineTypes - Published machine types from type-definitions search (`id` = type definition GUID, `name` = label) so the model can return `machineTypeDefinitionId`
   * @returns Observable containing machine analysis results
   */
  analyzeMachineImage(imageFile: File, availableMachineTypes?: MachineTypeDefinitionOption[]): Observable<MachineAnalysisResult | null> {
    this.logService.trackEvent("machine-image-analysis-started");

    return this.openAIService.analyzeMachineImage(imageFile, availableMachineTypes).pipe(
      tap(result => {
        if (result) {
          this.logService.trackEvent("machine-image-analysis-success", {
            machineType: result.machineType,
            machineTypeDefinitionId: result.machineTypeDefinitionId,
            make: result.make,
            confidence: result.confidence,
          });
        } else {
          this.logService.trackEvent("machine-image-analysis-failed");
        }
      }),
      catchError(error => {
        this.logService.error("machine-image-analysis", error);
        this.logService.trackEvent("machine-image-analysis-error");
        return of(null);
      }),
    );
  }

  getFinancingUrl(machine: InventoryItem): string {
    const baseUrl = "https://apply.paramountfinancial.com/";

    const params: { [key: string]: string } = {
      customer: "RUBBL",
      equipmentAmount: "",
      equipmentDescription: `${machine.year} ${machine.make} ${machine.model} - Rubbl ID: ${machine.id.substring(0, 8)}`,
    };

    const user = this.authenticationService.getUserNonAsync();
    if (user && user.profile) {
      if (user.profile.given_name) {
        params.firstName = user.profile.given_name;
      }
      if (user.profile.family_name) {
        params.lastName = user.profile.family_name;
      }
      if (user.profile.email) {
        params.email = user.profile.email;
      }
      if (user.profile.phone_number) {
        params.phone = user.profile.phone_number;
      }
    }

    const queryString = Object.keys(params)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join("&");

    return `${baseUrl}?${queryString}`;
  }
}
